import React, { useEffect } from "react";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { ScheduleDateType } from "../scheduledDateDropdown/scheduledDateDropdown";
import { DateTime } from "luxon";

export type ScheduleTimeType = {
  displayValue: string;
  timeObject: Date;
};

export const ScheduledTimeDropdown = ({
  scheduleTime,
  setScheduleTime,
  businessHours,
  scheduleDate,
  leadTime,
  timezone,
  orderType,
}: {
  scheduleTime: ScheduleTimeType;
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  businessHours: any;
  scheduleDate: ScheduleDateType;
  leadTime: any;
  timezone: string;
  orderType: string;
}) => {
  const nowInMerchantZone = DateTime.now().setZone(timezone);
  const timeOptions: any[] = [];
  const day = DateTime.fromJSDate(scheduleDate?.dateObject, {
    zone: timezone,
  }).weekday;
  const daysOfWeek = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const dayName = daysOfWeek[day];
  const { deliveryTime, pickupTime } = JSON.parse(
    global?.window?.localStorage.getItem("orderEstimates") || "{}"
  );

  const HHMMtoDate = (HHMM: string, timezone: string, date: Date): DateTime => {
    const [hours, minutes] = [HHMM.slice(0, 2), HHMM.slice(2, 4)];
    const dateInMerchantZone = DateTime.fromJSDate(date, { zone: timezone });
    return dateInMerchantZone.set({
      hour: parseInt(hours, 10),
      minute: parseInt(minutes, 10),
      second: 0,
      millisecond: 0,
    });
  };

  const generateTimeOptions = (
    timeRanges: { start: string; end: string }[],
    date: Date
  ) => {
    const currentDayInMerchantZone = DateTime.fromJSDate(date, {
      zone: timezone,
    });
    const deliveryDelay =
      orderType === "delivery"
        ? Math.round(
            (new Date(deliveryTime).getTime() -
              new Date(pickupTime).getTime()) /
              1000 /
              60
          )
        : 0;

    // This happens when order type is changed to delivery but delivery address is not entered
    // if (!deliveryDelay) {
    //   return;
    // }

    timeRanges.forEach(({ start, end }) => {
      let startTime = HHMMtoDate(start.padStart(4, "0"), timezone, date);
      const endTime = HHMMtoDate(end.padStart(4, "0"), timezone, date);

      let leadTimeThreshold = roundToNext15Minutes(
        startTime.plus({ minutes: leadTime + deliveryDelay })
      );

      if (
        currentDayInMerchantZone.hasSame(nowInMerchantZone, "day") &&
        leadTimeThreshold < nowInMerchantZone
      ) {
        leadTimeThreshold = roundToNext15Minutes(
          nowInMerchantZone.plus({ minutes: leadTime + deliveryDelay })
        );
      } else {
        leadTimeThreshold =
          orderType === "delivery"
            ? leadTimeThreshold
            : leadTimeThreshold.plus({ minutes: 15 });
      }

      for (
        let time = leadTimeThreshold;
        time <= endTime;
        time = time.plus({ minutes: 15 })
      ) {
        timeOptions.push({
          displayValue: time.toLocaleString(DateTime.TIME_SIMPLE),
          timeObject: time.toJSDate(),
        });
      }
    });
  };

  if (businessHours && businessHours[dayName]) {
    const todayTimeRanges = businessHours[dayName];
    generateTimeOptions(todayTimeRanges, scheduleDate?.dateObject);
  }

  useEffect(() => {
    if (timeOptions.length > 0 && !scheduleTime?.displayValue) {
      setScheduleTime(timeOptions[0]);
    }
  }, [scheduleDate, orderType]);

  return (
    <div key={scheduleDate?.dateObject.toISOString()}>
      <Popover className="relative">
        <PopoverButton
          className={[
            "flex",
            "items-center",
            "gap-x-2",
            "w-full",
            "leading-6",
            "text-gray-900",
            "hover:bg-gray-50",
          ].join(" ")}
        >
          <input
            type="text"
            id="scheduled_time_drop"
            className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border border-black hover:border-black focus:outline-none focus:ring-1 focus:ring-blue peer cursor-pointer"
            value={scheduleTime.displayValue}
            readOnly
          />
          <label
            htmlFor="scheduled_time_drop"
            className="absolute text-md text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
          >
            Time
          </label>
        </PopoverButton>
        <PopoverPanel
          transition
          className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
          style={{ maxHeight: "400px", overflowY: "auto" }}
        >
          <div className="p-3">
            {timeOptions.map((time) => (
              <CloseButton
                key={time.displayValue}
                className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 flex-auto cursor-pointer"
                onClick={() => setScheduleTime(time)}
              >
                <p className="text-[15px]">{time.displayValue}</p>
              </CloseButton>
            ))}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
};

const roundToNext15Minutes = (dateTime: DateTime): DateTime => {
  const minutes = dateTime.minute;
  const remainder = minutes % 15;
  if (remainder === 0) return dateTime.startOf("minute");
  return dateTime.plus({ minutes: 15 - remainder }).startOf("minute");
};
